/* E-signature for engagement letters (roadmap item 8). The firm places fields
   on a PDF, one listed contact signs it in the portal, and the signed copy
   carries a signing certificate on its last page. These are the rules both
   sides share; the PDF work and the hash chain live in the API and the
   database, because they need node's crypto. */

export const signatureFieldKinds = [
  "signature",
  "date_signed",
  "full_name",
] as const
export type SignatureFieldKind = (typeof signatureFieldKinds)[number]

export const signatureStatuses = [
  "awaiting_signature",
  "signed",
  "voided",
] as const
export type SignatureStatus = (typeof signatureStatuses)[number]

export const signatureVoidReasons = [
  "replaced",
  "canceled",
  "item_removed",
  "contact_removed",
] as const
export type SignatureVoidReason = (typeof signatureVoidReasons)[number]

export const signatureEventTypes = [
  "sent",
  "viewed",
  "consented",
  "signed",
  "completed",
  "voided",
] as const
export type SignatureEventType = (typeof signatureEventTypes)[number]

export const signatureMethods = ["typed", "drawn"] as const
export type SignatureMethod = (typeof signatureMethods)[number]

/** `not_required` means malware scanning was off when the original came in. */
export const signatureScanStatuses = [
  "pending",
  "clean",
  "malicious",
  "not_required",
] as const
export type SignatureScanStatus = (typeof signatureScanStatuses)[number]

/** How the signer proved they hold the contact's email address. */
export const signatureSignInMethods = ["email_code", "email_link"] as const
export type SignatureSignInMethod = (typeof signatureSignInMethods)[number]

export const signatureMaxBytes = 10 * 1024 * 1024
export const signatureMaxPages = 100
export const signatureMaxFields = 30
export const signatureDrawnPngMaxBytes = 200 * 1024
/** Smallest box, in PDF points, a field may take on the page. */
export const signatureFieldMinSize = 12
export const signatureConsentVersion = "SIGNATURE_CONSENT_V1"

/** One page of the original, in PDF user space. `left` and `bottom` are the
 * lower-left corner of the page's crop box, which is not always 0,0. */
export type SignaturePage = Readonly<{
  width: number
  height: number
  rotate: 0 | 90 | 180 | 270
  left: number
  bottom: number
}>

/** A box on one page (1-based), in unrotated PDF user space. */
export type SignatureField = Readonly<{
  kind: SignatureFieldKind
  page: number
  x: number
  y: number
  width: number
  height: number
}>

export const isSignatureOriginalSignable = (
  scanStatus: SignatureScanStatus
): boolean => scanStatus === "clean" || scanStatus === "not_required"

export const normalizePageRotation = (
  rotate: number
): SignaturePage["rotate"] => {
  const turned = (((Math.round(rotate / 90) * 90) % 360) + 360) % 360
  return turned as SignaturePage["rotate"]
}

/** Why a field layout cannot be saved, or null when every field fits. */
export const signatureFieldsProblem = (
  fields: readonly SignatureField[],
  pages: readonly SignaturePage[]
): string | null => {
  if (fields.length > signatureMaxFields) {
    return `Place up to ${signatureMaxFields} fields.`
  }
  for (const field of fields) {
    const page = pages[field.page - 1]
    if (!Number.isInteger(field.page) || !page) {
      return "A field is on a page the document does not have."
    }
    const numbers = [field.x, field.y, field.width, field.height]
    if (!numbers.every(Number.isFinite)) return "A field has no position."
    if (
      field.width < signatureFieldMinSize ||
      field.height < signatureFieldMinSize
    ) {
      return "A field is too small to sign in."
    }
    const slack = 0.5
    if (
      field.x < page.left - slack ||
      field.y < page.bottom - slack ||
      field.x + field.width > page.left + page.width + slack ||
      field.y + field.height > page.bottom + page.height + slack
    ) {
      return "A field runs off its page."
    }
  }
  return null
}

/** JSON with sorted object keys at every depth, so the same event always
 * hashes the same. Undefined members are dropped, as JSON.stringify does. */
export const canonicalJson = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value ?? null)
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => canonicalJson(entry)).join(",")}]`
  }
  const record = value as Record<string, unknown>
  const members = Object.keys(record)
    .filter((key) => record[key] !== undefined)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
  return `{${members.join(",")}}`
}

/** The date a signature field shows, in the firm's time zone:
 * "September 24, 2026". */
export const formatSignatureDate = (at: Date, timeZone: string): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(at)

export type SignatureConsentCopy = Readonly<{
  version: typeof signatureConsentVersion
  title: string
  paragraphs: string[]
  checkbox: string
  intent: string
}>

/** The ESIGN 7001(c) disclosure the signer agrees to before Sign. The words
 * are stored by version: a change here is a new version, never an edit. */
export const signatureConsentCopy = (input: {
  documentName: string
  firmName: string
}): SignatureConsentCopy => ({
  version: signatureConsentVersion,
  title: "Sign electronically",
  paragraphs: [
    `You agree to sign ${input.documentName} electronically. Your electronic signature has the same effect as your handwritten signature. This agreement covers this document only.`,
    `You can ask ${input.firmName} for a paper copy to sign by hand instead, at no charge. Send them a message here. You can withdraw this agreement at any time before you sign. After you sign, withdrawing does not undo the signature.`,
    "To sign and keep a copy, you need a current web browser and a way to open PDF files. After you sign, the signed copy stays in your files here and you can download it.",
    `Keep your email address up to date with ${input.firmName} so you get notices about it.`,
  ],
  checkbox: "I agree to sign electronically",
  intent: `By selecting Sign, you sign ${input.documentName} with the signature above.`,
})

/** The name the client knows a letter by: the item's name, as the task list
 * shows it, else the uploaded file's name without ".pdf". The signing screen,
 * the consent, the certificate and the signed copy all use it. */
export const signatureDocumentName = (input: {
  itemName?: string | null
  fileName: string
}): string =>
  input.itemName?.trim() || input.fileName.replace(/\.pdf$/i, "").trim() || "Document"

/** The file name of the signed copy: "Engagement letter (signed).pdf". A
 * slash or a control character in the name would read as a folder or break
 * the download, so it becomes a space. */
export const signedCopyFileName = (documentName: string): string =>
  `${
    documentName
      .replace(/\.pdf$/i, "")
      .replace(/[\u0000-\u001f\u007f/\\]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "Document"
  } (signed).pdf`
